import { useEffect, useState } from "react";

/** url → "r, g, b" triple, so components can build alpha variants via rgba(). */
const cache = new Map<string, string>();

function hslToRgbTriple(h: number, s: number, l: number): string {
  const a = s * Math.min(l, 1 - l);
  const f = (n: number) => {
    const k = (n + h / 30) % 12;
    return Math.round((l - a * Math.max(-1, Math.min(k - 3, 9 - k, 1))) * 255);
  };
  return `${f(0)}, ${f(8)}, ${f(4)}`;
}

/** Deterministic fallback color so every provider gets a stable tint even if extraction fails. */
function hashColor(seed: string): string {
  let h = 0;
  for (let i = 0; i < seed.length; i++) h = (h * 31 + seed.charCodeAt(i)) | 0;
  return hslToRgbTriple(((h % 360) + 360) % 360, 0.62, 0.46);
}

/**
 * Dominant color of the logo, weighted by chroma so the brand color wins over
 * greys/white. Returns null when the image is effectively white/transparent
 * (nothing usable to tint with) or the canvas is CORS-tainted.
 */
function extractDominantColor(img: HTMLImageElement): string | null {
  try {
    const size = 24;
    const canvas = document.createElement("canvas");
    canvas.width = size;
    canvas.height = size;
    const ctx = canvas.getContext("2d");
    if (!ctx) return null;
    ctx.drawImage(img, 0, 0, size, size);
    const { data } = ctx.getImageData(0, 0, size, size);

    let r = 0;
    let g = 0;
    let b = 0;
    let total = 0;
    for (let i = 0; i < data.length; i += 4) {
      const alpha = data[i + 3] / 255;
      if (alpha < 0.5) continue;
      const [pr, pg, pb] = [data[i], data[i + 1], data[i + 2]];
      if (pr > 235 && pg > 235 && pb > 235) continue; // near-white background
      const chroma = Math.max(pr, pg, pb) - Math.min(pr, pg, pb);
      const weight = (chroma + 12) * alpha; // +12 keeps monochrome logos (black/grey) usable
      r += pr * weight;
      g += pg * weight;
      b += pb * weight;
      total += weight;
    }
    if (total < 1) return null;

    let [cr, cg, cb] = [r / total, g / total, b / total];
    // Too light to contrast with the white icon chip → darken proportionally.
    const luminance = (0.299 * cr + 0.587 * cg + 0.114 * cb) / 255;
    if (luminance > 0.72) {
      const scale = 0.72 / luminance;
      cr *= scale;
      cg *= scale;
      cb *= scale;
    }
    return `${Math.round(cr)}, ${Math.round(cg)}, ${Math.round(cb)}`;
  } catch {
    return null; // CORS-tainted canvas
  }
}

/**
 * Accent color ("r, g, b") for a provider icon: dominant color of the image when
 * extractable, otherwise a stable hash-derived color from `seed`. Null without iconUrl.
 */
export function useIconAccent(iconUrl: string | null | undefined, seed: string): string | null {
  const [color, setColor] = useState<string | null>(() =>
    iconUrl ? (cache.get(iconUrl) ?? hashColor(seed)) : null,
  );

  useEffect(() => {
    if (!iconUrl) {
      setColor(null);
      return;
    }
    const cached = cache.get(iconUrl);
    if (cached) {
      setColor(cached);
      return;
    }
    setColor(hashColor(seed));

    let cancelled = false;
    const img = new Image();
    img.crossOrigin = "anonymous";
    img.onload = () => {
      const extracted = extractDominantColor(img);
      if (!extracted) return;
      cache.set(iconUrl, extracted);
      if (!cancelled) setColor(extracted);
    };
    img.src = iconUrl;
    return () => {
      cancelled = true;
    };
  }, [iconUrl, seed]);

  return color;
}
